export const missing = "—";
export function price(value) {
  if (value == null || !Number.isFinite(Number(value))) return missing;
  const digits = Math.abs(value) < 10 ? 4 : Math.abs(value) < 1000 ? 2 : 0;
  return "$" + Number(value).toLocaleString("en-US", {minimumFractionDigits: digits, maximumFractionDigits: digits});
}
export function percent(value, digits = 2, signed = false) {
  return value == null || !Number.isFinite(Number(value)) ? missing : `${signed && value >= 0 ? "+" : ""}${Number(value).toFixed(digits)}%`;
}
export const funding = value => value == null ? missing : `${percent(value, 4, true)} per 8h`;
export const bps = value => value == null ? missing : `${Number(value).toFixed(2)} bps`;
export const annualized = value => value == null ? missing : `${percent(value)} annualized`;
export function money(value) {
  if (value == null || !Number.isFinite(Number(value))) return missing;
  for (const [scale, suffix] of [[1e12,"T"],[1e9,"B"],[1e6,"M"]]) if (Math.abs(value) >= scale) return `${value < 0 ? "−" : ""}$${(Math.abs(value) / scale).toFixed(2)}${suffix}`;
  return price(value);
}
export function cleanNumbers(text) { return String(text || "").replace(/([+-]?)0+(\d+\.\d+)%/g, (_, sign, value) => sign + Number(value).toString() + "%"); }

export function editorialStories(edition) {
  return (edition?.stories || []).filter(story => {
    const source = edition.sources?.[story.sourceIndex - 1];
    if (!source) return true; // legacy editions without sourceIndex remain readable
    const text = source.title + ' ' + story.text;
    return !/newsletter|roundup|live blog|live news:|sponsored|here.s what happened in crypto today|daily trends and events|highlighted in the latest news update|hodler.s digest|crypto week ahead/i.test(text)
      && /bitcoin|\bBTC\b|ethereum|\bETH\b|solana|\bSOL\b|\bXRP\b|crypto|stablecoin|coinbase|binance|ETF|Strategy|STRC|token|blockchain|Fed|FOMC|CPI|PCE|yields?|dollar|oil|Nasdaq|S&P/i.test(text);
  }).map(story => {
    const source = edition.sources?.[story.sourceIndex-1];
    if (!source || story.tag !== 'Network') return story;
    const title = source.title;
    const tag = /flaw|hack|breach|exploit|vulnerab|rogue servers/i.test(title) ? 'Security'
      : /tax|regulat|consultation|rules|sanctions|SEC|CFTC/i.test(title) ? 'Regulation'
      : /Strategy|STRC|BlackRock|Robinhood|CalPERS|institution/i.test(title) ? 'Institutions'
      : /ETF/i.test(title) ? 'ETF' : /Fed|FOMC|CPI|PCE|yields?|oil|Nasdaq|S&P/i.test(title) ? 'Macro'
      : /funding|open interest|options|liquidation/i.test(title) ? 'Derivatives'
      : /upgrade|outage|fork|hashrate|gas fees|mainnet|validator/i.test(title) ? 'Network' : 'Other';
    return {...story,tag};
  });
}

export function observation(row, now = new Date()) {
  const date = row.observationDate || row.context?.match(/(?:Observation|Trading) date (\d{4}-\d{2}-\d{2})/i)?.[1];
  if (!date) return {date: null, stale: false};
  const start = new Date(date + "T00:00:00Z"), end = new Date(now.toISOString().slice(0,10) + "T00:00:00Z");
  let days = (end - start) / 86400000;
  if (/S&P|Nasdaq|index/i.test(row.metric) && !/dollar/i.test(row.metric)) {
    days = 0; for (let day = new Date(start); day < end;) { day.setUTCDate(day.getUTCDate()+1); if (![0,6].includes(day.getUTCDay())) days++; }
  }
  return {date, stale: days > (/dollar/i.test(row.metric) ? 7 : 1)};
}

export function normalizeLevels(level, tape = []) {
  const tick = tape.find(item => item.label === level.asset);
  const spot = tick ? Number(String(tick.value).replace(/[$,]/g,"")) : level.spot;
  if (!Number.isFinite(spot) || spot <= 0) return level;
  const items = [...(level.support || []), ...(level.resistance || []), ...(level.currentRange || [])].map(band => {
    const parsed = String(band.range).match(/[\d,]+(?:\.\d+)?/g) || [];
    return {...band, low: band.low ?? Number(parsed[0]?.replace(/,/g,"")), high: band.high ?? Number(parsed[1]?.replace(/,/g,""))};
  }).filter(band => Number.isFinite(band.low) && Number.isFinite(band.high) && band.high > band.low).sort((a,b) => a.low-b.low);
  const merged = [];
  for (const item of items) {
    const previous = merged[merged.length-1];
    if (previous && item.low <= previous.high) { previous.high = Math.max(previous.high,item.high); previous.reasons.push(...(item.reasons?.length ? item.reasons : [item.note])); }
    else merged.push({...item, reasons: item.reasons?.length ? [...item.reasons] : [item.note]});
  }
  const result = {...level, spot, support: [], resistance: [], currentRange: []};
  for (const item of merged) {
    const side = item.low > spot ? "resistance" : item.high < spot ? "support" : "currentRange";
    item.range = `${price(item.low)}–${price(item.high)}`;
    item.reasons = [...new Set(item.reasons.flatMap(reason => String(reason || "").split(/;| · /)).filter(Boolean))].sort((a,b) => (a.includes("30-day") ? 0 : a.includes("daily tests") ? 1 : 2) - (b.includes("30-day") ? 0 : b.includes("daily tests") ? 1 : 2));
    item.distancePct = ((side === "resistance" ? item.low : side === "support" ? item.high : spot)/spot-1)*100;
    result[side].push(item);
  }
  return result;
}
